/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState, useEffect } from "react";
import { MediaBatchData } from "../../../types/mediaBatchCommon.types";
import { useTranslation } from "react-i18next";
import { ValidationErrors } from "../../../types/mediaBatchPreparation.types";
import Select from "react-select";

interface FineTuningDetails {
	id: string;
	media_batch_id: string;
	expected_final_ph: string;
	observed_final_ph: string;
	ph_within_range: string;
	visual_standards_met: string;
	remarks: string;
	file_id: string | null;
	created_by: string;
	created_at: string;
	updated_by: string | null;
	updated_at: string;
}

interface Props {
	mediaBatchData: MediaBatchData & { fine_tuning_details?: FineTuningDetails } | null;
	handlePayloadChange: (field: string, value: string | number | boolean) => void;
	phVerified: boolean;
	setPhVerified: (value: boolean) => void;
	phReading: string | number;
	isReadOnly: boolean;
	batchList: any[];
    onBatchChange: (batchId: string) => void;
	errors: ValidationErrors;
	setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
	onSearch: (keyword: string) => void;
}

const FinalSolidificationAudit = ({
	mediaBatchData,
	handlePayloadChange,
	phVerified,
	setPhVerified,
	phReading,
	isReadOnly,
	batchList,
	onBatchChange,
	errors,
	setErrors,
	onSearch
	
}: Props) => {
	const { t } = useTranslation();
	const details = mediaBatchData?.fine_tuning_details;
	const powderConsumption = mediaBatchData?.powder_consumption?.[0];

	// Extract display values. If read-only, force the saved values.
	const displayPhReading = isReadOnly ? (details?.observed_final_ph || "") : phReading;
	const displayPhVerified = isReadOnly ? (String(details?.ph_within_range) === "t") : phVerified;

	// Local state initialization
	const [visualClear, setVisualClear] = useState(() => {
		return String(details?.visual_standards_met) === "t";
	});

	const displayVisualClear = isReadOnly ? (String(details?.visual_standards_met) === "t") : visualClear;

	const toggleVisualStandards = () => {
		if (isReadOnly) return;
		setVisualClear((p) => {
			const newValue = !p;
			handlePayloadChange("visual_standards_met", newValue);
			if (newValue) {
				setErrors(prev => ({
					...prev,
					visualStandards: "",
				}));
			}
			return newValue;
		});
	};

	const togglePhVerified = () => {
		if (isReadOnly) return;

		const newValue = !phVerified;
		setPhVerified(newValue);

		if (newValue) {
			setErrors(prev => ({
				...prev,
				observedPh: "",
			}));
		}
	};


	return (
		<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
			<div className="bg-[#2D3E50] px-6 py-3 flex justify-between items-center">
				<div className="flex items-center gap-3">
					<div className="w-6 h-6 rounded-md bg-white/20 text-white flex items-center justify-center text-[10px] font-black">
						01
					</div>
					<h3 className="text-xs font-black text-white uppercase tracking-widest mt-1">
						{t("mediaBatchInit.fineTuning.finalSolidificationAudit")}
					</h3>
				</div>
				<div className="flex items-center gap-2">
					<span className="text-[10px] font-black text-white/70 uppercase tracking-widest">
						{t("mediaBatchInit.finalQuality.batch")}:
					</span>

					<div className="relative">
						<Select
							className="w-[180px] text-[12px]"
							value={
							batchList
								.map((batch) => ({
								value: batch.id,
								label: batch.media_batch_identifier,
								}))
								.find((o) => o.value === mediaBatchData?.id)
							}
							options={batchList.map((batch) => ({
							value: batch.id,
							label: batch.media_batch_identifier,
							}))}
							onInputChange={(value, { action }) => {
								if (action === "input-change") onSearch(value);
							}}
							onChange={(option) => {
							if (option) {
								onBatchChange(String(option.value));
							}
							}}
							isSearchable
							styles={{
							control: (base, state) => ({
							...base,
							minHeight: "36px",
							height: "36px",
							border: state.isFocused ? "1px solid #4B5563" : "1px solid #D1D5DB",
							borderRadius: "8px",
							boxShadow: "none",
							"&:hover": { border: "1px solid #4B5563" },
							}),
							valueContainer: (base) => ({ ...base, height: "36px", padding: "0 12px", display: "flex", alignItems: "center" }),
							singleValue: (base) => ({ ...base, margin: 0, position: "static" }),
							input: (base) => ({ ...base, margin: 0, padding: 0 }),
							indicatorsContainer: (base) => ({ ...base, height: "36px" }),
						}}
						/>

						<span className="material-symbols-outlined absolute right-0.5 top-1/2 -translate-y-1/2 text-white/70 text-[18px] pointer-events-none">
							expand_more
						</span>
					</div>
				</div>
			</div>
			<div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-12">
				<div className="space-y-6">
					<div className="space-y-3">
						<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
							{t("mediaBatchInit.fineTuning.finalPhCheck")}  <span className="text-red-500">*</span>
						</p>
						<div className="flex gap-4">
							<div className={`flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-center ${isReadOnly ? 'opacity-70' : ''}`}>
								<p className="text-[9px] font-black text-slate-400 uppercase mb-0">
									{t("mediaBatchInit.fineTuning.reading")}  <span className="text-red-500">*</span>
								</p>
								<input
									type="number"
									value={displayPhReading}
									disabled={isReadOnly}
									onChange={(e) =>
										handlePayloadChange(
											"observed_final_ph",
											parseFloat(e.target.value) || e.target.value,
										)
									}
									className="text-3xl font-black text-[#2D3E50] bg-transparent text-center w-full focus:outline-none border-none disabled:bg-transparent disabled:cursor-not-allowed mt-3"
									placeholder="0.00"
								/>
							</div>
							<div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
								<p className="text-[9px] font-black text-slate-400 uppercase mb-0">
									{t("mediaBatchInit.fineTuning.target")}
								</p>
								<p className="text-2xl font-black text-emerald-600">
									{powderConsumption?.final_ph} ±{" "}
									{powderConsumption?.final_ph_tolerance}
								</p>
							</div>
						</div>
						<button
							onClick={togglePhVerified}
							disabled={isReadOnly}
							className={`w-full py-3 rounded-lg text-[10px] font-black uppercase transition-all flex items-center justify-center gap-2 border-none disabled:cursor-not-allowed disabled:opacity-80 ${displayPhVerified ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
						>
							{displayPhVerified ? (
								<span className="material-symbols-outlined text-sm">
									check_circle
								</span>
							) : null}
							{displayPhVerified
								? t("mediaBatchInit.fineTuning.verifiedWithinRange")
								: t("mediaBatchInit.fineTuning.verifiedpHCalibration")}
						</button>
						{errors.observedPh && (<p className="mt-2 text-[11px] text-red-500">{errors.observedPh}</p>)}
					</div>
				</div>

				<div className="space-y-6">
					<div className="space-y-3">
						<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
							{t("mediaBatchInit.fineTuning.mediaAppearance")}  <span className="text-red-500">*</span>
						</p>
						<div className="grid grid-cols-2 gap-3">
							{[
								{ label: t("mediaBatchInit.fineTuning.colorMatch"), icon: "palette" },
								{ label: t("mediaBatchInit.fineTuning.noBubbles"), icon: "blur_off" },
								{ label: t("mediaBatchInit.fineTuning.homogeneous"), icon: "texture" },
								{ label: t("mediaBatchInit.fineTuning.solidSurface"), icon: "square" },
							].map((attr, i) => (
								<div
									key={i}
									className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100 mb-0.5"
								>
									<span className="material-symbols-outlined text-sm text-slate-400">
										{attr.icon}
									</span>
									<span className="text-[10px] font-bold text-slate-700">
										{attr.label}
									</span>
								</div>
							))}
						</div>
						<button
							onClick={toggleVisualStandards}
							disabled={isReadOnly}
							className={`w-full py-3 rounded-lg text-[10px] font-black uppercase transition-all flex items-center justify-center gap-2 border-none disabled:cursor-not-allowed disabled:opacity-80 ${displayVisualClear ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
						>
							{displayVisualClear ? (
								<span className="material-symbols-outlined text-sm">
									visibility
								</span>
							) : null}
							{displayVisualClear
								? t("mediaBatchInit.fineTuning.visualStandardsMet")
								: t("mediaBatchInit.fineTuning.certifyVisualClarity")}
						</button>
						{errors.visualStandards && (<p className="mt-2 text-[11px] text-red-500">{errors.visualStandards}</p>)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default FinalSolidificationAudit;
